import { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  BarChart3,
  TrendingUp,
  TrendingDown,
  DollarSign,
  ShoppingCart,
  Users,
  Clock,
  RefreshCw,
  Filter,
  ChevronDown,
  Calendar,
  ArrowUpRight,
  ArrowDownRight,
  CreditCard,
  Banknote,
  RotateCcw,
  XCircle,
  Package,
} from 'lucide-react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Bar, Doughnut, Line } from 'react-chartjs-2';
import apiService from '../services/api';
import PosCaisseSharedHeader from '../components/caisse/PosCaisseSharedHeader';
import { usePosDisplayTimeZone } from '../hooks/usePosDisplayTimeZone';

ChartJS.register(
  CategoryScale, LinearScale, BarElement, LineElement,
  PointElement, ArcElement, Title, Tooltip, Legend, Filler,
);

const PERIOD_OPTIONS = [
  { value: 'today', label: "Aujourd'hui" },
  { value: 'yesterday', label: 'Hier' },
  { value: 'week', label: 'Cette semaine' },
  { value: 'month', label: 'Ce mois' },
  { value: 'last30', label: '30 derniers jours' },
  { value: 'custom', label: 'Personnalisé' },
];

const CANAL_OPTIONS = [
  { value: '', label: 'Tous les canaux' },
  { value: 'POS', label: 'Caisse (POS)' },
  { value: 'WEB', label: 'Web' },
  { value: 'APP', label: 'Application' },
  { value: 'ADMIN', label: 'Admin' },
];

function fmtYmdLocal(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function computePeriodDates(period) {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const todayYmd = fmtYmdLocal(today);
  switch (period) {
    case 'yesterday': {
      const y = new Date(today); y.setDate(y.getDate() - 1);
      const ymd = fmtYmdLocal(y);
      return { dateDebut: ymd, dateFin: ymd };
    }
    case 'week': {
      const d = new Date(today);
      const day = d.getDay();
      d.setDate(d.getDate() - (day === 0 ? 6 : day - 1));
      return { dateDebut: fmtYmdLocal(d), dateFin: todayYmd };
    }
    case 'month': {
      const m = new Date(now.getFullYear(), now.getMonth(), 1);
      return { dateDebut: fmtYmdLocal(m), dateFin: todayYmd };
    }
    case 'last30': {
      const d = new Date(today); d.setDate(d.getDate() - 30);
      return { dateDebut: fmtYmdLocal(d), dateFin: todayYmd };
    }
    default:
      return { dateDebut: todayYmd, dateFin: todayYmd };
  }
}

const fmt = (v) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(v || 0);
const fmtPct = (v) => `${(v || 0).toFixed(1)}%`;
const fmtDuration = (sec) => {
  if (!sec) return '-';
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  return h > 0 ? `${h}h${m.toString().padStart(2, '0')}` : `${m} min`;
};

function KpiCard({ title, value, subtitle, icon: Icon, color = 'indigo', trend }) {
  const colorMap = {
    indigo: 'bg-indigo-50 text-indigo-600',
    green: 'bg-green-50 text-green-600',
    red: 'bg-red-50 text-red-600',
    amber: 'bg-amber-50 text-amber-600',
    blue: 'bg-blue-50 text-blue-600',
    purple: 'bg-purple-50 text-purple-600',
  };
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">{title}</p>
          <p className="mt-2 text-2xl font-bold text-gray-900 truncate">{value}</p>
          {subtitle && <p className="mt-1 text-sm text-gray-500">{subtitle}</p>}
        </div>
        <div className={`ml-3 p-2.5 rounded-lg ${colorMap[color] || colorMap.indigo}`}>
          <Icon className="h-5 w-5" />
        </div>
      </div>
      {trend !== undefined && trend !== null && (
        <div className={`mt-3 flex items-center text-xs font-medium ${trend >= 0 ? 'text-green-600' : 'text-red-600'}`}>
          {trend >= 0 ? <ArrowUpRight className="h-3.5 w-3.5 mr-0.5" /> : <ArrowDownRight className="h-3.5 w-3.5 mr-0.5" />}
          {fmtPct(Math.abs(trend))} vs session prec.
        </div>
      )}
    </div>
  );
}

const CaisseKpi = () => {
  const [searchParams] = useSearchParams();
  const commercantIdParam = searchParams.get('commercantId');
  const posClock = usePosDisplayTimeZone();

  const [kpi, setKpi] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [period, setPeriod] = useState('today');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [canalVente, setCanalVente] = useState('');
  const [sessionId, setSessionId] = useState('');
  const [vendeurId, setVendeurId] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const getCommercantId = useCallback(() => {
    if (commercantIdParam) return commercantIdParam;
    try {
      const u = JSON.parse(localStorage.getItem('admin_user') || '{}');
      return u.commercantId || u.commercant?.id || null;
    } catch { return null; }
  }, [commercantIdParam]);

  const loadKpi = useCallback(async () => {
    const cid = getCommercantId();
    if (!cid) { setError('Aucun commerçant sélectionné'); setLoading(false); return; }

    setLoading(true);
    setError(null);
    try {
      const dates = period === 'custom'
        ? { dateDebut: customStart || undefined, dateFin: customEnd || undefined }
        : computePeriodDates(period);

      const params = {
        commercantId: cid,
        ...dates,
        ...(canalVente ? { canalVente } : {}),
        ...(sessionId ? { sessionId } : {}),
        ...(vendeurId ? { vendeurId } : {}),
      };

      const res = await apiService.getCaisseKpi(params);
      setKpi(res.kpi);
    } catch (err) {
      console.error('Erreur KPI:', err);
      setError(err.message || 'Erreur de chargement des KPI');
    } finally {
      setLoading(false);
    }
  }, [getCommercantId, period, customStart, customEnd, canalVente, sessionId, vendeurId]);

  useEffect(() => { loadKpi(); }, [loadKpi]);

  // Graphique ventes/heure
  const ventesHeureData = useMemo(() => {
    if (!kpi?.ventesParHeure) return null;
    const labels = kpi.ventesParHeure.map((h) => `${h.heure}h`);
    return {
      labels,
      datasets: [
        {
          label: 'CA',
          data: kpi.ventesParHeure.map((h) => h.montant),
          backgroundColor: 'rgba(99, 102, 241, 0.6)',
          borderColor: 'rgb(99, 102, 241)',
          borderWidth: 1,
          borderRadius: 4,
          yAxisID: 'y',
        },
        {
          label: 'Tickets',
          data: kpi.ventesParHeure.map((h) => h.count),
          type: 'line',
          borderColor: 'rgb(234, 88, 12)',
          backgroundColor: 'rgba(234, 88, 12, 0.1)',
          fill: true,
          tension: 0.3,
          pointRadius: 2,
          yAxisID: 'y1',
        },
      ],
    };
  }, [kpi]);

  // Graphique paiements par methode (doughnut)
  const paiementMethodeData = useMemo(() => {
    if (!kpi?.parMethode) return null;
    const labels = [];
    const data = [];
    const colors = [];
    const methodColors = {
      ESPECES: { bg: 'rgba(16, 185, 129, 0.8)', label: 'Espèces' },
      CARTE: { bg: 'rgba(99, 102, 241, 0.8)', label: 'Carte' },
      CHEQUE: { bg: 'rgba(245, 158, 11, 0.8)', label: 'Chèque' },
      VIREMENT: { bg: 'rgba(139, 92, 246, 0.8)', label: 'Virement' },
    };
    for (const [m, info] of Object.entries(methodColors)) {
      if (kpi.parMethode[m]?.montant > 0) {
        labels.push(info.label);
        data.push(kpi.parMethode[m].montant);
        colors.push(info.bg);
      }
    }
    if (data.length === 0) return null;
    return { labels, datasets: [{ data, backgroundColor: colors, borderWidth: 0 }] };
  }, [kpi]);

  // Sessions liste
  const sessionsList = kpi?.sessions?.liste || [];
  const vendeursList = kpi?.topVendeurs || [];
  const topProduitsList = kpi?.topProduits || [];

  if (error && !kpi) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <XCircle className="h-12 w-12 text-red-400 mx-auto mb-3" />
          <p className="text-red-600 font-medium">{error}</p>
          <button onClick={loadKpi} className="mt-3 text-sm text-indigo-600 hover:text-indigo-800 underline">Réessayer</button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PosCaisseSharedHeader className="-mx-4 sm:-mx-6 lg:-mx-8 mb-2" commercant={posClock.commercant} />
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">KPI Caisse</h1>
          <p className="text-sm text-gray-500 mt-1">
            {kpi?.periode ? `${new Date(kpi.periode.debut).toLocaleDateString('fr-FR')} - ${new Date(kpi.periode.fin).toLocaleDateString('fr-FR')}` : 'Chargement...'}
            {kpi?.filtres?.canalVente && kpi.filtres.canalVente !== 'TOUS' && ` | Canal : ${kpi.filtres.canalVente}`}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select
            value={period}
            onChange={(e) => { setPeriod(e.target.value); if (e.target.value === 'custom') setShowFilters(true); }}
            className="text-sm border border-gray-300 rounded-lg px-3 py-2 bg-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          >
            {PERIOD_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          {period === 'custom' && (
            <>
              <input
                type="date"
                value={customStart}
                onChange={(e) => setCustomStart(e.target.value)}
                className="text-sm border border-gray-300 rounded-lg px-3 py-2 bg-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="Début"
              />
              <span className="text-gray-400 text-sm">-</span>
              <input
                type="date"
                value={customEnd}
                onChange={(e) => setCustomEnd(e.target.value)}
                className="text-sm border border-gray-300 rounded-lg px-3 py-2 bg-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="Fin"
              />
            </>
          )}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg border transition-colors ${showFilters ? 'bg-indigo-50 border-indigo-300 text-indigo-700' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'}`}
          >
            <Filter className="h-4 w-4" /> Filtres <ChevronDown className={`h-3.5 w-3.5 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
          </button>
          <button
            onClick={loadKpi}
            disabled={loading}
            className="flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 transition-colors"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Actualiser
          </button>
        </div>
      </div>

      {/* Filtres avances */}
      {showFilters && (
        <div className="bg-white rounded-xl border border-gray-200 p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Canal de vente</label>
            <select value={canalVente} onChange={(e) => setCanalVente(e.target.value)} className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2">
              {CANAL_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          {sessionsList.length > 0 && (
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Session</label>
              <select value={sessionId} onChange={(e) => setSessionId(e.target.value)} className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2">
                <option value="">Toutes les sessions</option>
                {sessionsList.map((s) => (
                  <option key={s.id} value={s.id}>
                    {new Date(s.dateOuverture).toLocaleString('fr-FR', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit' })} - {s.statut}
                  </option>
                ))}
              </select>
            </div>
          )}
          {vendeursList.length > 0 && (
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Vendeur</label>
              <select value={vendeurId} onChange={(e) => setVendeurId(e.target.value)} className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2">
                <option value="">Tous les vendeurs</option>
                {vendeursList.map((v) => (
                  <option key={v.vendeurId} value={v.vendeurId}>{v.prenom} {v.nom}</option>
                ))}
              </select>
            </div>
          )}
        </div>
      )}

      {loading && !kpi ? (
        <div className="flex items-center justify-center min-h-[300px]">
          <RefreshCw className="h-8 w-8 text-indigo-400 animate-spin" />
        </div>
      ) : kpi ? (
        <>
          {/* Cartes KPI */}
          <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
            <KpiCard title="CA Brut" value={fmt(kpi.ca.brut)} icon={DollarSign} color="green" subtitle={`${kpi.tickets.count} ticket${kpi.tickets.count > 1 ? 's' : ''}`} />
            <KpiCard title="CA Net" value={fmt(kpi.ca.net)} icon={TrendingUp} color="indigo" subtitle={kpi.ca.remboursements > 0 ? `- ${fmt(kpi.ca.remboursements)} remb.` : 'Aucun remboursement'} />
            <KpiCard title="Panier moyen" value={fmt(kpi.panierMoyen)} icon={ShoppingCart} color="blue" />
            <KpiCard title="Remboursements" value={fmt(kpi.remboursements.total)} icon={RotateCcw} color="red" subtitle={`${kpi.remboursements.count} opération${kpi.remboursements.count > 1 ? 's' : ''}`} />
            <KpiCard title="Annulations" value={String(kpi.tickets.annulations?.total ?? kpi.tickets.annulations ?? 0)} icon={XCircle} color="amber" subtitle={
              kpi.tickets.annulations?.apresPaiement > 0
                ? `${kpi.tickets.annulations.apresPaiement} post-paiement · ${kpi.tickets.annulations.avantPaiement || 0} non payé${(kpi.tickets.annulations.avantPaiement || 0) > 1 ? 's' : ''}`
                : kpi.tickets.annulations?.avantPaiement > 0
                  ? `${kpi.tickets.annulations.avantPaiement} non payé${kpi.tickets.annulations.avantPaiement > 1 ? 's' : ''}`
                  : undefined
            } />
            <KpiCard title="Sessions" value={String(kpi.sessions.count)} icon={Clock} color="purple" subtitle={kpi.sessions.dureeMoyenne ? `Moy. ${fmtDuration(kpi.sessions.dureeMoyenne)}` : undefined} />
          </div>

          {/* Graphiques */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Ventes par heure */}
            <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
              <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
                <Clock className="h-4 w-4 text-indigo-500" /> Ventes par heure
              </h3>
              {ventesHeureData ? (
                <div className="h-64">
                  <Bar data={ventesHeureData} options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: { legend: { position: 'top', labels: { boxWidth: 12, font: { size: 11 } } } },
                    scales: {
                      y: { position: 'left', title: { display: true, text: 'CA (€)', font: { size: 10 } }, ticks: { font: { size: 10 } } },
                      y1: { position: 'right', grid: { drawOnChartArea: false }, title: { display: true, text: 'Tickets', font: { size: 10 } }, ticks: { font: { size: 10 }, stepSize: 1 } },
                      x: { ticks: { font: { size: 10 } } },
                    },
                  }} />
                </div>
              ) : (
                <p className="text-sm text-gray-400 text-center py-12">Aucune donnée</p>
              )}
            </div>

            {/* Repartition par methode */}
            <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
              <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
                <CreditCard className="h-4 w-4 text-indigo-500" /> Méthodes de paiement
              </h3>
              {paiementMethodeData ? (
                <div className="h-52 flex items-center justify-center">
                  <Doughnut data={paiementMethodeData} options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: { position: 'bottom', labels: { boxWidth: 10, font: { size: 11 }, padding: 12 } },
                      tooltip: { callbacks: { label: (ctx) => `${ctx.label}: ${fmt(ctx.raw)}` } },
                    },
                    cutout: '60%',
                  }} />
                </div>
              ) : (
                <p className="text-sm text-gray-400 text-center py-12">Aucune donnée</p>
              )}
              {kpi?.parMethode && (
                <div className="mt-3 space-y-1.5 text-xs text-gray-600">
                  {Object.entries(kpi.parMethode).filter(([, v]) => v.montant > 0).map(([m, v]) => (
                    <div key={m} className="flex justify-between">
                      <span>{m === 'ESPECES' ? 'Espèces' : m === 'CARTE' ? 'Carte' : m === 'CHEQUE' ? 'Chèque' : 'Virement'}</span>
                      <span className="font-medium">{fmt(v.montant)} <span className="text-gray-400">({v.count})</span></span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Tableaux */}
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
            {/* Top produits */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-100">
                <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                  <Package className="h-4 w-4 text-indigo-500" /> Top Produits
                </h3>
              </div>
              {topProduitsList.length > 0 ? (
                <div className="divide-y divide-gray-50">
                  {topProduitsList.slice(0, 10).map((p, i) => (
                    <div key={p.produitId} className="px-5 py-3 flex items-center gap-3 hover:bg-gray-50">
                      <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full bg-indigo-50 text-indigo-600 text-xs font-bold">{i + 1}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{p.nom}</p>
                        <p className="text-xs text-gray-500">{p.quantite} vendu{p.quantite > 1 ? 's' : ''}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-semibold text-gray-900">{fmt(p.ca)}</p>
                        <p className="text-xs text-gray-400">{fmtPct(p.pctCa)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-400 text-center py-8">Aucun produit</p>
              )}
            </div>

            {/* Top vendeurs */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-100">
                <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                  <Users className="h-4 w-4 text-indigo-500" /> Top Vendeurs
                </h3>
              </div>
              {vendeursList.length > 0 ? (
                <div className="divide-y divide-gray-50">
                  {vendeursList.map((v, i) => (
                    <div key={v.vendeurId} className="px-5 py-3 flex items-center gap-3 hover:bg-gray-50">
                      <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full bg-purple-50 text-purple-600 text-xs font-bold">{i + 1}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{v.prenom} {v.nom}</p>
                        <p className="text-xs text-gray-500">{v.tickets} ticket{v.tickets > 1 ? 's' : ''} | Moy. {fmt(v.panierMoyen)}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-semibold text-gray-900">{fmt(v.ca)}</p>
                        {v.remboursements > 0 && (
                          <p className="text-xs text-red-500">-{fmt(v.remboursements)}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-400 text-center py-8">Aucun vendeur</p>
              )}
            </div>

            {/* Historique sessions */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-100">
                <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-indigo-500" /> Sessions
                  {kpi?.sessions?.ecartMoyen > 0 && (
                    <span className="text-xs text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full ml-auto">Ecart moy. {fmt(kpi.sessions.ecartMoyen)}</span>
                  )}
                </h3>
              </div>
              {sessionsList.length > 0 ? (
                <div className="divide-y divide-gray-50 max-h-80 overflow-y-auto">
                  {sessionsList.map((s) => {
                    const duree = s.dateFermeture ? Math.round((new Date(s.dateFermeture) - new Date(s.dateOuverture)) / 1000) : null;
                    return (
                      <div
                        key={s.id}
                        className={`px-5 py-3 hover:bg-gray-50 cursor-pointer ${sessionId === s.id ? 'bg-indigo-50 border-l-2 border-indigo-500' : ''}`}
                        onClick={() => setSessionId(sessionId === s.id ? '' : s.id)}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-gray-900">
                              {new Date(s.dateOuverture).toLocaleDateString('fr-FR')} {new Date(s.dateOuverture).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                            </p>
                            <p className="text-xs text-gray-500 mt-0.5">
                              {s.ventesCount} vente{s.ventesCount > 1 ? 's' : ''}{duree ? ` | ${fmtDuration(duree)}` : ''}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-semibold text-gray-900">{fmt(s.totalVentes)}</p>
                            <span className={`inline-block text-xs px-1.5 py-0.5 rounded-full ${s.statut === 'FERMEE' ? 'bg-green-50 text-green-700' : s.statut === 'OUVERTE' ? 'bg-blue-50 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>
                              {s.statut === 'FERMEE' ? 'Fermée' : s.statut === 'OUVERTE' ? 'Active' : s.statut}
                            </span>
                          </div>
                        </div>
                        {s.ecartCaisse !== 0 && s.ecartCaisse != null && (
                          <p className={`text-xs mt-1 ${Math.abs(s.ecartCaisse) > 1 ? 'text-red-500' : 'text-amber-500'}`}>
                            Ecart : {fmt(s.ecartCaisse)}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm text-gray-400 text-center py-8">Aucune session</p>
              )}
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
};

export default CaisseKpi;
